<template>
  <footer
    v-if="!isWebviewUserAgent"
    :class="[
      'footer',
    ]"
  >
    <div v-if="minimal" class="minimal-footer-wrapper">
      <p class="t4 center-text">
        {{ $t('onboarding_copyright_reserved') }}
      </p>
    </div>
    <div v-else>
      <div class="footer-links">
        <div class="legal-links">
          <span data-testid="link_termsAndConditions"
                class="legal-link"
                v-html="termsAndConditionsHandler"
          ></span>
          <span v-if="showHipaaConsent" data-testid="link_hipaaConsent"
                class="legal-link"
                v-html="hipaaHandler"
          ></span>
          <span data-testid="link_privacyStatement"
                class="legal-link"
                v-html="privacyStatementHandler"
          ></span>
          <span data-testid="link_nonDiscrimination"
                class="legal-link"
                v-html="nonDescriminationHandler"
          ></span>
        </div>
        <div class="help-links">
          <span class="info-link">{{ $t('onboarding_help_email_label') }}</span>
          <a :href="supportMailTo"
             class="info-link"
             data-testid="link_supportEmail"
          >
            <span class="support-link">{{ supportEmail }}</span>
          </a>
        </div>
      </div>
      <div class="footer-disclaimers">
        <div class="legal-disclaimer">
          <p class="legal-info" data-testid="text_legalDisclaimer">
            {{ $t('onboarding_disclaimer') }}
          </p>
        </div>
        <div class="copyright-links">
          <p class="info-link" data-testid="text_copyrightDisclaimer">
            {{ $t('onboarding_copyright_reserved') }}
          </p>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import { mapGetters } from 'vuex';
import isWebview from 'is-ua-webview';

import { CONFIGS } from '@/scripts/constants';
import { env } from '@/config';
import { CONFIGURATIONS } from '@/scripts/constants/configurations';
import { clientStoreTypes, applicationStoreTypes } from '@/store/types';
import eco from '@/mixins/eco';

export default {
  name: 'SWORDFooter',
  mixins: [eco],
  props: {
    links: {
      type: Object,
      default: () => ({}),
    },
    minimal: Boolean,
  },
  data() {
    return {
      termsOfUseURL: env.VUE_APP_TERMS_OF_USE_URL,
      noticeOfPrivacyURL: env.VUE_APP_NOTICE_OF_PRIVACY_URL,
      informedConsentURL: env.VUE_APP_INFORMED_CONSENT_URL,
      nonDiscriminationURL: env.VUE_APP_NON_DISCRIMINATION_URL,
    };
  },
  computed: {
    ...mapGetters({
      clientLegalLinks: clientStoreTypes.getters.GET_CLIENT_LEGAL_LINKS,
      clientConfiguration: clientStoreTypes.getters.GET_CLIENT_CONFIGURATION,
      supportEmailByUnit: applicationStoreTypes.getters.SUPPORT_EMAIL_BY_UNIT,
      unit: applicationStoreTypes.getters.UNIT,
    }),
    isWebviewUserAgent() {
      return isWebview(navigator.userAgent);
    },
    termsAndConditionsHandler() {
      const { clientLegalLinks, termsOfUseURL } = this;

      const TERMS_AND_CONDITIONS_URL = clientLegalLinks?.termsAndConditions ?? termsOfUseURL;


      return this.$t('onboarding.footer_terms', {
        TERMS_AND_CONDITIONS_URL,
      });
    },
    hipaaHandler() {
      const { clientLegalLinks, noticeOfPrivacyURL } = this;

      const HIPAA_NOTICE_OF_PRIVACY_PRACTICES_URL = clientLegalLinks?.hipaaConsent ?? noticeOfPrivacyURL;


      return this.$t('onboarding.footer_hipaa', {
        HIPAA_NOTICE_OF_PRIVACY_PRACTICES_URL,
      });
    },
    privacyStatementHandler() {
      const { clientLegalLinks, informedConsentURL } = this;

      const PRIVACY_STATEMENT_URL = clientLegalLinks?.privacyStatement ?? informedConsentURL;

      return this.$t('onboarding.footer_privacystatement', {
        PRIVACY_STATEMENT_URL,
      });
    },
    nonDescriminationHandler() {
      const { clientLegalLinks, nonDiscriminationURL } = this;

      const NON_DISCRIMINATION_URL = clientLegalLinks?.nonDiscrimination ?? nonDiscriminationURL;

      return this.$t('onboarding_footer_non_discrimination', {
        NON_DISCRIMINATION_URL,
      });
    },
    supportEmail() {
      if (this.isEcoExperience) {
        return this.clientConfiguration(CONFIGURATIONS.SUPPORT_EMAIL) || 'help@swordhealth.com';
      }

      return this.supportEmailByUnit;
    },
    supportMailTo() {
      return `mailto:${this.supportEmail}`;
    },
    showHipaaConsent() {
      return CONFIGS.VUE_APP_SHOW_HIPAA_CONSENT === 'true';
    },
  },
  methods: {
    hideLink(name) {
      return this.linksToHide.includes(name);
    },
  },
};
</script>
<style lang="scss" scoped>
.footer {
  padding: 2em 2.5em;
  background: $background-color-dark;

  .minimal-footer-wrapper {
    display: flex;
    justify-content: center;
  }

  .footer-links {
    display: flex;
    justify-content: space-between;

    .legal-links {
      display: flex;
      justify-content: flex-start;

      ::v-deep a {
        color: $context-color-1;
        margin-right: 1em;
        font-size: 0.75rem;
      }
    }

    .info-link,

    .help-links {
      display: flex;
      justify-content: center;

      &:after {
        content: '';
        width: 1px;
        height: 1rem;
        float: right;
        margin: 0 1rem;
        border-right: 1px solid getColor($greys, 1);
        display: none;
      }
    }

    .support-link {
      color: $secondary-color;
    }

    .help-links {
      align-items: flex-end;
      gap: .5rem;

      a,
      .info-link {
        width: auto;
        display: inline-block;
      }
    }
  }

  .footer-disclaimers {
    display: flex;
    justify-content: space-between;
    margin-top: 2rem;

    .legal-disclaimer {
      flex-basis: 70%;
      max-width: 48rem;
    }

    .copyright-links {
      margin-bottom: 1.5rem;
    }
  }

  p {
    display: inline;
  }

  .legal-link > a {
    color: blue;
  }

  .legal-link,
  .a,
  .info-link,
  .legal-disclaimer {
    @extend %t3;
    @extend .fc-blue-8;
    line-height: 1;
  }

  .legal-info {
    font-size: 0.625rem;
    line-height: 1.7;
  }

  .info-link {
    font-size: 0.75rem;
  }

  // Only desktop
  @media screen and (min-width: $min-desktop-size) {
    .legal-links {
      flex-basis: 70%;
    }
    .footer-disclaimers {
      .copyright-links {
        white-space: nowrap;
        padding-left: 2rem;
      }
    }
  }

  // Only Tablet
  @media screen and (min-width: $min-tablet-size) and (max-width: $max-tablet-size) {
    .footer-links {
      flex-direction: column;

      .help-links a {
        width: 100%;
        text-align: center;
        margin-top: 2rem;
      }

      .legal-links {
        justify-content: space-between;
      }
    }
  }

  // Smaller Tablet and bigger
  @media screen and (min-width: $min-tablet-size) {
  }

  // Bigger Tablet and smaller
  @media screen and (max-width: $max-tablet-size) {
    .footer-disclaimers {
      flex-direction: column-reverse;
      text-align: center;
    }
  }

  // Until bigger phone
  @media screen and (max-width: $max-smartphone-size) {
    padding: 2em 1em;
    .footer-links,
    .footer-links .legal-links {
      flex-direction: column;
    }

    .legal-link {
      align-self: center;
      margin-bottom: 1rem;
      &:last-child {
        margin-bottom: 1rem;
      }
    }

    .legal-links,
    .help-links {
      width: 100%;
    }

    .footer-disclaimers {
      margin-top: 1.5rem;
    }
  }
}
</style>
